import { faChevronLeft, faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Autocomplete, Box, Button, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, TextField, Typography, useMediaQuery } from "@mui/material";
import { useEffect, useState } from "react";
import { CidadeTriagem } from "../../../interfaces/entities/CidadeTriagem";
import { EstadoTriagem } from "../../../interfaces/entities/EstadoTriagem";
import { Horarios } from "../../../interfaces/entities/Horarios";
import { TipoAtendRetorno } from "../../../interfaces/entities/PAVirtual/TipoAtendRetorno";
import { TipoOrientacaoTriagem } from "../../../interfaces/entities/TipoOrientacaoTriagem";
import { getCidadeTriagem, getEstadoTriagem, getHorariosDisponiveisPeriodoPa } from "../../../services/SaudeOnlineService";
import ModalCidadesTriagem from "./ModalCidadesTriagem";
import ScreeningCalendar from "./ScreeningCalendar";
import ScreeningHours from "./ScreeningHours";
import { sxAutocomplete, sxBoxInsideCoverageCitiesButton, sxBoxScreening, sxButton, sxRadioCitiesBox, sxScreeningFormLabel, sxSelectedCityInfo } from "./sxProps";
import { useAccessibility } from "../../../Store/contexts/AccessibilityContext";
import CitiesListModal from "../AgendarConsulta/CitiesListModal";
import { getStateFullName } from "../../../utils/StateMap";

const StepLocalExames = (props: {
  backStep: () => void,
  nextStep: (cidade: CidadeTriagem | null, horarioAgendamentoInfantil: Horarios | null) => void,
  sintoma: TipoOrientacaoTriagem | null,
  tipoAtendPaVirtual: TipoAtendRetorno
}) => {

  const accessibility = useAccessibility();

  const { backStep, nextStep, sintoma, tipoAtendPaVirtual } = { ...props };
  const mobileScreen = useMediaQuery('(max-width: 768px)');
  const [estados, setEstados] = useState<EstadoTriagem[] | null>(null);
  const [estadoSelecionado, setEstadoSelecionado] = useState<EstadoTriagem | null>(null);
  const [cidades, setCidades] = useState<CidadeTriagem[] | null>(null);
  const [cidadeSelecionada, setCidadeSelecionada] = useState<CidadeTriagem | null>(null);
  const [showModalCidadesTriagem, setShowModalCidadesTriagem] = useState<boolean>(false);
  const [coverageArea, setCoverageArea] = useState<'inside' | 'outside' | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [hoursList, setHoursList] = useState<Horarios[]>([]);
  const [selectedHour, setSelectedHour] = useState<Horarios | null>(null);

  useEffect(() => {
    const fetchEstados = async () => {
      let estadosResponse = await getEstadoTriagem();
      if (estadosResponse?.status === 200) {
        setEstados(estadosResponse.data);
      }
    }

    fetchEstados();
  }, [])


  const handleCoverageAreaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCidadeSelecionada(null);
    setEstadoSelecionado(null);
    setCidades([]);
    setCoverageArea(event.target.value as 'inside' | 'outside');
  };

  const handleShowModalCidadesTriagem = () => {
    setShowModalCidadesTriagem(showModal => !showModal);
  }

  const handleModalAreaCobertura = async () => {
    setCoverageArea('inside');
    let cidadesResponse = await getCidadeTriagem('');
    if (cidadesResponse?.status === 200) {
      setCidades(cidadesResponse.data);
    }
    handleShowModalCidadesTriagem();
  }

  const selectInsideCoverageAreaCity = (city: CidadeTriagem | null) => {
    setShowModalCidadesTriagem(false);
    setCidadeSelecionada(city);
  };

  const handleProceed = () => {
    nextStep(cidadeSelecionada, selectedHour);
  }

  const handleUfChange = async (event: React.SyntheticEvent, value: EstadoTriagem | null) => {
    setEstadoSelecionado(value);
    if (value !== null) {
      const result = await getCidadeTriagem(value.idUf);
      if (result?.status === 200) {
        setCidadeSelecionada(null);
        setCidades(result.data)
      }
    }
    else {
      setCidades([]);
    }
  }

  const selectOutsideCoverageAreaCity = (event: React.SyntheticEvent, value: CidadeTriagem | null) => {
    setCidadeSelecionada(value)
  }

  const handleDate = (date: Date) => {
    setSelectedDate(date)
  }

  const handleMonth = async (start: Date, end: Date) => {
    try {
      const result = await getHorariosDisponiveisPeriodoPa(start, end);
      if (result?.status === 200) {
        let horariosDisponiveis = result.data;
        setHoursList(horariosDisponiveis);
      }
    } catch (error) {
      console.error('ERRO ao buscar horários disponíveis: ', error);
    }
  }

  const handleHour = (hour: Horarios) => {
    setSelectedHour(hour);
  }

  return (
    <>
      <Box sx={sxBoxScreening}>
        <Typography component="h2" color="#383838" style={accessibility.textStyleAccessibility} fontFamily={'Unimed-Sans'}>
          <b>Sintoma: {sintoma!.descr}</b>
        </Typography>
        <FormControl component={"fieldset"} key={"city-selection"}>
          <FormLabel id={"label-city"} style={accessibility.textStyleAccessibility} sx={sxScreeningFormLabel}>Caso seja necessária a realização de exames, selecione o local:</FormLabel>
          <RadioGroup
            sx={{ maxWidth: '70%' }}
            value={coverageArea}
            onChange={handleCoverageAreaChange}
          >
            <Box sx={sxRadioCitiesBox}>
              <FormControlLabel
                key='inside-coverage-area-cities'
                value='inside'
                control={<Radio style={accessibility.textStyleAccessibility} />}
                label={<p className="coverage-area-text pa-virtual-option" style={accessibility.textStyleAccessibility}>Cidade <b>na área de cobertura</b> da Unimed Campinas</p>}
              />
              <Box sx={sxBoxInsideCoverageCitiesButton}>
                <button className='inside-coverage-area-cities-button' style={accessibility.textStyleAccessibility} tabIndex={0} disabled={coverageArea === 'outside'} onClick={handleModalAreaCobertura}>
                  [Lista de cidades]
                </button>
                {(cidadeSelecionada && coverageArea === 'inside') && <Typography style={accessibility.textStyleAccessibility} sx={sxSelectedCityInfo}>{cidadeSelecionada.descr}</Typography>}
              </Box>
            </Box>
            <FormControlLabel
              key='out-coverage-area-cities'
              value='outside'
              control={<Radio style={accessibility.textStyleAccessibility} />}
              label={<p className="coverage-area-text pa-virtual-option" style={accessibility.textStyleAccessibility}>Cidade <b>fora da área de cobertura</b> da Unimed Campinas</p>}
            />
          </RadioGroup>
        </FormControl>
        {coverageArea === 'outside' &&
          <div className="outside-area-location-holder">
            <div className="outside-area-location-autocomplete">
              <h1 className="outside-area-location-title" style={accessibility.textStyleAccessibility}>Estado</h1>
              <label htmlFor='autocomplete-states' aria-label='estado' />
              <Autocomplete
                disablePortal
                fullWidth
                id="autocomplete-states"
                onChange={handleUfChange}
                options={estados || []}
                value={estadoSelecionado}
                getOptionLabel={(option: EstadoTriagem) => option.siglaUf}
                isOptionEqualToValue={(option, value) => option.idUf === value.idUf}
                sx={sxAutocomplete}
                style={accessibility.textStyleAccessibility}
                renderInput={(params) => <TextField {...params} />}
                renderOption={(props, option) => (
                  <li {...props} aria-label={getStateFullName(option.siglaUf) || ''}>
                    {option.siglaUf} 
                  </li>
                )}
              />
            </div>
            <div className="outside-area-location-autocomplete">
              <h1 className="outside-area-location-title" style={accessibility.textStyleAccessibility}>Cidade</h1>
              <label htmlFor='autocomplete-outside-cities' aria-label='cidade' />
              <Autocomplete
                disablePortal
                fullWidth
                id="autocomplete-outside-cities"
                onChange={selectOutsideCoverageAreaCity}
                options={cidades || []}
                value={cidadeSelecionada}
                getOptionLabel={(option: CidadeTriagem) => option.descr}
                isOptionEqualToValue={(option, value) => option.idCidade === value.idCidade}
                sx={sxAutocomplete}
                renderInput={(params) => <TextField {...params} />}
              />
            </div>
          </div>
        }
        {(tipoAtendPaVirtual === 'VIRTUAL-AGENDA' && ((coverageArea === 'outside' && cidadeSelecionada) || (coverageArea === 'inside'))) &&
          <div className="calendar-holder">
            <h1 className='select-date-title'>Selecione uma data no calendário</h1>
            <div className="calendar-selection">
              <ScreeningCalendar handleDate={handleDate} handleMonth={handleMonth} selectedDate={selectedDate} hoursList={hoursList} />
              {selectedDate !== null &&
                <div className="forward-holder">
                  <div className="forward">
                    <FontAwesomeIcon icon={faChevronRight} />
                    <FontAwesomeIcon icon={faChevronRight} />
                  </div>
                </div>
              }
              <ScreeningHours selectedDate={selectedDate} handleHour={handleHour} selectedHour={selectedHour} hoursList={hoursList} />
            </div>
          </div>
        }
        <Button
          variant="contained"
          size="large"
          onClick={handleProceed}
          sx={sxButton}
          style={accessibility.buttonStyleAccessibility}
          disabled={
            !coverageArea || (coverageArea === 'outside' && !cidadeSelecionada) || (tipoAtendPaVirtual === 'VIRTUAL-AGENDA' && !selectedHour)
          }
          fullWidth={mobileScreen}>
            <span style={accessibility.buttonTextStyleAccessibility}>
              PROSSEGUIR
            </span>
        </Button>
        <button className="back-button" onClick={backStep} style={accessibility.textStyleAccessibility} aria-label="voltar para questionário">
          <FontAwesomeIcon icon={faChevronLeft} style={accessibility.textStyleAccessibility} />&nbsp;&nbsp;<u>Voltar</u>
        </button>
      </Box>
      <CitiesListModal 
        open={showModalCidadesTriagem} 
        handleClose={() => {setShowModalCidadesTriagem(false)}} 
        handleCity={city => selectInsideCoverageAreaCity({ descr: city.descr, idCidade: city.idCidade.toString()})} 
        closeCities={cidades?.map(c => ({ descr: c.descr, idCidade: parseInt(c.idCidade)})) || []} 
      />
    </>
  )

}

export default StepLocalExames;